import { useState } from "react";
import { Check, Play } from "lucide-react";
import { AnimatePresence } from "framer-motion";
import { useLanguage } from "@/context/LanguageContext";
import { TEAM, aboutCopy } from "@/data/about";
import { KitModal, MarioModal, ZambiasiModal, EduardoModal } from "@/components/TeamModals";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import { CtaBand } from "./Home";

const MODALS = {
  "Kit Lynch Robinson": KitModal,
  "Mario Tagliabue": MarioModal,
  "Massimo Zambiasi": ZambiasiModal,
  "Eduardo Vieitez": EduardoModal,
};

export default function ChiSiamo() {
  const { lang } = useLanguage();
  const t = aboutCopy[lang];
  const [openModal, setOpenModal] = useState(null);

  return (
    <main data-testid="about-page">
      <Seo
        title={lang === "it" ? "RV FILM — Chi siamo" : "RV FILM — About us"}
        description={t.intro1}
      />

      <section className="relative pt-20 md:pt-28 pb-16 px-6 lg:px-12 overflow-hidden">
        <div className="absolute -right-40 -top-20 w-[480px] h-[480px] spotlight-red" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto">
          <Reveal y={12}>
            <p className="f-mono text-[11px] uppercase tracking-[0.3em] text-red-500 mb-6">{t.kicker}</p>
            <h1 className="f-display font-black tracking-tight leading-[0.95] text-[12vw] sm:text-6xl lg:text-8xl" data-testid="about-title">
              {t.titleA}
              <span className="f-serif italic font-normal text-red-500 block">{t.titleB}</span>
            </h1>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl">
            <Reveal delay={0.15}>
              <p className="text-slate-400 text-base leading-relaxed">{t.intro1}</p>
            </Reveal>
            <Reveal delay={0.25}>
              <p className="text-slate-400 text-base leading-relaxed">{t.intro2}</p>
            </Reveal>
          </div>
        </div>
      </section>

      <Marquee />

      <section className="py-24 md:py-32 px-6 lg:px-12" data-testid="team-section">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <h2 className="f-display text-3xl sm:text-4xl font-bold tracking-tight mb-14">{t.teamTitle}</h2>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
            {TEAM.map((m, i) => {
              const Modal = MODALS[m.name];
              const inner = (
                <>
                  <div className={`rounded-lg overflow-hidden border border-white/10 aspect-square bg-[#101114] relative ${Modal ? "group-hover:border-red-600/60 transition-colors duration-500" : ""}`}>
                    <img
                      src={m.img}
                      alt={m.name}
                      loading="lazy"
                      className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
                    />
                    {Modal && (
                      <span className="absolute bottom-3 right-3 w-9 h-9 rounded-full bg-red-600 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <Play size={13} fill="white" className="text-white ml-0.5" />
                      </span>
                    )}
                  </div>
                  <p className="mt-4 f-mono text-[10px] uppercase tracking-[0.2em] text-red-500">
                    {lang === "it" ? m.roleIt : m.roleEn}
                  </p>
                  <h3 className="mt-1 f-display text-xl md:text-2xl font-extrabold tracking-tight">{m.name}</h3>
                  {Modal && (
                    <p className="mt-1 f-mono text-[9px] uppercase tracking-[0.2em] text-slate-500 group-hover:text-red-500 transition-colors">
                      {lang === "it" ? "Guarda il portfolio →" : "View portfolio →"}
                    </p>
                  )}
                </>
              );
              return (
                <Reveal key={m.name} delay={(i % 3) * 0.08}>
                  {Modal ? (
                    <button
                      onClick={() => setOpenModal(m.name)}
                      className="group text-left w-full"
                      data-testid={`team-portfolio-${m.name.split(" ")[0].toLowerCase()}`}
                      aria-label={`${m.name} portfolio`}
                    >
                      {inner}
                    </button>
                  ) : (
                    <div className="group" data-testid={`team-member-${i}`}>
                      {inner}
                    </div>
                  )}
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 px-6 lg:px-12 bg-[#0B0B0E]" data-testid="about-method">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16">
          <div>
            <Reveal>
              <h2 className="f-display text-3xl sm:text-4xl font-bold tracking-tight">{t.doTitle}</h2>
              <p className="mt-6 text-slate-400 leading-relaxed">{t.doIntro}</p>
            </Reveal>
            <ul className="mt-6 space-y-4">
              {t.doPoints.map((p, i) => (
                <Reveal key={p} delay={0.08 + i * 0.06}>
                  <li className="flex items-start gap-3 text-slate-300 text-sm md:text-base" data-testid={`about-do-point-${i}`}>
                    <Check size={18} className="text-red-500 mt-0.5 shrink-0" />
                    {p}
                  </li>
                </Reveal>
              ))}
            </ul>
            <Reveal delay={0.3}>
              <p className="mt-6 text-slate-400 leading-relaxed">{t.doOutro}</p>
            </Reveal>
          </div>
          <div>
            <Reveal>
              <h2 className="f-display text-3xl sm:text-4xl font-bold tracking-tight">{t.approachTitle}</h2>
              <p className="mt-6 text-slate-400 leading-relaxed">{t.approachIntro}</p>
            </Reveal>
            <div className="mt-8">
              {t.approachSteps.map((s, i) => (
                <Reveal key={s} delay={0.08 + i * 0.06}>
                  <div className="flex items-center gap-6 py-5 border-t border-white/10 group" data-testid={`about-approach-step-${i}`}>
                    <span className="f-mono text-sm text-red-500">0{i + 1}</span>
                    <span className="f-display text-lg md:text-xl font-bold tracking-tight group-hover:text-red-500 transition-colors duration-300">
                      {s}
                    </span>
                  </div>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.35}>
              <p className="mt-6 text-slate-400 leading-relaxed">{t.approachOutro}</p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 px-6 lg:px-12" data-testid="about-why">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <h2 className="f-display text-3xl sm:text-4xl font-bold tracking-tight">{t.whyTitle}</h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 rounded-lg overflow-hidden">
            {t.whyPoints.map((p, i) => (
              <Reveal key={p} delay={i * 0.07} className="bg-[#101114] p-8">
                <p className="f-mono text-[10px] uppercase tracking-[0.25em] text-red-500 mb-4">0{i + 1}</p>
                <p className="f-display text-lg font-bold tracking-tight leading-snug" data-testid={`about-why-point-${i}`}>{p}</p>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <p className="mt-10 f-serif italic text-xl md:text-2xl text-slate-300 max-w-2xl">{t.whyOutro}</p>
          </Reveal>
        </div>
      </section>

      <CtaBand />

      <AnimatePresence>
        {openModal &&
          (() => {
            const Active = MODALS[openModal];
            return <Active onClose={() => setOpenModal(null)} lang={lang} />;
          })()}
      </AnimatePresence>
    </main>
  );
}
